import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  BusinessEntity,
  DocumentCategory,
  DocumentFolder,
  FinanceDocument,
  FinancialAccount,
} from '../../types/family-finance'
import { FOLDER_CATEGORIES, FOLDER_ICONS, FOLDER_LABELS } from '../../types/family-finance'
import {
  deleteDocument,
  getDocumentUrl,
  listAccounts,
  listDocuments,
  listEntities,
  setDocumentShared,
  updateDocument,
} from '../../lib/family-finance-api'
import DocumentUploader from './DocumentUploader'
import {
  Card,
  Chip,
  EmptyState,
  LoadError,
  LoadingRows,
  errorMessage,
  inputClass,
  primaryBtn,
  secondaryBtn,
  shortDate,
} from './shared'

const FOLDERS: DocumentFolder[] = ['taxes', 'businesses', 'accounts']
const TAX_YEARS = [2026, 2025, 2024, 2023, 2022, 2021]

function fileIcon(doc: FinanceDocument): string {
  const type = doc.mimeType
  if (type.includes('pdf')) return '📄'
  if (type.startsWith('image/')) return '🖼️'
  if (type.includes('sheet') || type.includes('csv') || type.includes('excel')) return '📊'
  if (type.includes('word') || type.includes('document')) return '📝'
  return '📎'
}

function sizeLabel(sizeKb: number): string {
  return sizeKb >= 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${sizeKb.toLocaleString()} KB`
}

// ─── Row editor ──────────────────────────────────────────────

function DocumentEditor({
  doc,
  entities,
  accounts,
  onSaved,
  onCancel,
}: {
  doc: FinanceDocument
  entities: BusinessEntity[]
  accounts: FinancialAccount[]
  onSaved: () => void
  onCancel: () => void
}) {
  const [form, setForm] = useState({
    name: doc.name,
    folder: doc.folder,
    category: doc.category,
    taxYear: doc.taxYear,
    entityId: doc.entityId,
    accountId: doc.accountId,
    source: doc.source,
    notes: doc.notes,
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function setFolder(folder: DocumentFolder) {
    const allowed = FOLDER_CATEGORIES[folder]
    setForm((f) => ({
      ...f,
      folder,
      category: allowed.includes(f.category) ? f.category : allowed[allowed.length - 1],
    }))
  }

  async function save() {
    if (!form.name.trim()) {
      setError('A document name is required.')
      return
    }
    if (form.folder === 'businesses' && !form.entityId) {
      setError('Pick the business this document belongs to.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await updateDocument(doc.id, { ...form, name: form.name.trim() })
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bg-slate-100/70 dark:bg-slate-900/70 rounded-xl p-3 mt-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <label className="block sm:col-span-2">
          <span className="text-slate-500 text-xs">Name</span>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={`${inputClass} mt-1`}
          />
        </label>
        <label className="block">
          <span className="text-slate-500 text-xs">Folder</span>
          <select
            value={form.folder}
            onChange={(e) => setFolder(e.target.value as DocumentFolder)}
            className={`${inputClass} mt-1`}
          >
            {FOLDERS.map((f) => (
              <option key={f} value={f}>{FOLDER_LABELS[f]}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-slate-500 text-xs">Type</span>
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value as DocumentCategory })}
            className={`${inputClass} mt-1`}
          >
            {FOLDER_CATEGORIES[form.folder].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-slate-500 text-xs">Tax year</span>
          <select
            value={form.taxYear ?? ''}
            onChange={(e) =>
              setForm({ ...form, taxYear: e.target.value ? parseInt(e.target.value, 10) : null })
            }
            className={`${inputClass} mt-1`}
          >
            <option value="">— none —</option>
            {TAX_YEARS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-slate-500 text-xs">Business</span>
          <select
            value={form.entityId ?? ''}
            onChange={(e) => setForm({ ...form, entityId: e.target.value || null })}
            className={`${inputClass} mt-1`}
          >
            <option value="">— none —</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>{e.shortName}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-slate-500 text-xs">Account</span>
          <select
            value={form.accountId ?? ''}
            onChange={(e) => setForm({ ...form, accountId: e.target.value || null })}
            className={`${inputClass} mt-1`}
          >
            <option value="">— none —</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.institutionName} — {a.name}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-slate-500 text-xs">Source</span>
          <input
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
            className={`${inputClass} mt-1`}
          />
        </label>
        <label className="block sm:col-span-2 lg:col-span-4">
          <span className="text-slate-500 text-xs">Notes</span>
          <input
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className={`${inputClass} mt-1`}
          />
        </label>
      </div>
      {error && <p className="text-red-600 dark:text-red-400 text-sm mt-2">{error}</p>}
      <div className="flex items-center gap-2 mt-3">
        <button onClick={save} disabled={busy} className={primaryBtn}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        <button onClick={onCancel} disabled={busy} className={secondaryBtn}>Cancel</button>
      </div>
    </div>
  )
}

// ─── Document list ───────────────────────────────────────────

export function DocumentList({
  docs,
  entities,
  accounts,
  onChanged,
  emptyMessage = 'No documents here yet — upload one above.',
}: {
  docs: FinanceDocument[]
  entities: BusinessEntity[]
  accounts: FinancialAccount[]
  onChanged: () => void
  emptyMessage?: string
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const label = useMemo(() => {
    const ents = new Map(entities.map((e) => [e.id, e.shortName]))
    const accs = new Map(accounts.map((a) => [a.id, `${a.institutionName} ·· ${a.mask}`]))
    return (doc: FinanceDocument) => {
      const parts = [FOLDER_LABELS[doc.folder]]
      if (doc.folder === 'taxes' && doc.taxYear) parts.push(`TY ${doc.taxYear}`)
      if (doc.entityId && ents.has(doc.entityId)) parts.push(ents.get(doc.entityId)!)
      if (doc.accountId && accs.has(doc.accountId)) parts.push(accs.get(doc.accountId)!)
      return parts.join(' / ')
    }
  }, [accounts, entities])

  async function openFile(doc: FinanceDocument, download: boolean) {
    setBusy(doc.id)
    setNotice(null)
    try {
      const url = await getDocumentUrl(doc)
      if (!url) {
        setNotice(
          `“${doc.name}” is a record without a stored file — re-upload it to open or download it.`,
        )
        return
      }
      if (download) {
        const anchor = window.document.createElement('a')
        anchor.href = url
        anchor.download = doc.fileName
        anchor.click()
      } else {
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    } finally {
      setBusy(null)
    }
  }

  async function toggleShared(doc: FinanceDocument) {
    setBusy(doc.id)
    try {
      await setDocumentShared(doc.id, !doc.sharedWithCpa)
      onChanged()
    } finally {
      setBusy(null)
    }
  }

  async function remove(doc: FinanceDocument) {
    if (!window.confirm(`Delete “${doc.name}”? This removes the file and its record.`)) return
    setBusy(doc.id)
    try {
      await deleteDocument(doc.id)
      onChanged()
    } finally {
      setBusy(null)
    }
  }

  if (docs.length === 0) return <EmptyState icon="🗂️" message={emptyMessage} />

  return (
    <div>
      {notice && (
        <p className="text-amber-600 dark:text-amber-400 text-sm bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-3">
          {notice}
        </p>
      )}
      <div className="space-y-1">
        {docs.map((doc) => (
          <div
            key={doc.id}
            className="border-b border-slate-200/50 dark:border-slate-700/50 last:border-0 py-2.5"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5 min-w-0">
                <span className="text-lg leading-none mt-0.5">{fileIcon(doc)}</span>
                <div className="min-w-0">
                  <p className="text-slate-900 dark:text-white text-sm truncate">{doc.name}</p>
                  <p className="text-slate-500 text-xs">
                    {label(doc)} · {sizeLabel(doc.sizeKb)} · {shortDate(doc.uploadedAt)}
                    {doc.source && ` · ${doc.source}`}
                  </p>
                  {doc.notes && <p className="text-slate-400 dark:text-slate-500 text-xs mt-0.5">{doc.notes}</p>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Chip tone="slate">{doc.category}</Chip>
                {!doc.hasFile && <Chip tone="amber">no file</Chip>}
                <button
                  onClick={() => toggleShared(doc)}
                  disabled={busy === doc.id}
                  title={doc.sharedWithCpa ? 'In CPA folder — click to remove' : 'Add to CPA folder'}
                  className={`text-base px-1 ${doc.sharedWithCpa ? '' : 'opacity-30 grayscale'}`}
                >
                  👁
                </button>
                <button
                  onClick={() => openFile(doc, false)}
                  disabled={busy === doc.id}
                  title="Open in a new tab"
                  className="text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 text-sm px-1"
                >
                  Open
                </button>
                <button
                  onClick={() => openFile(doc, true)}
                  disabled={busy === doc.id}
                  title="Download"
                  className="text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 text-sm px-1"
                >
                  ↓
                </button>
                <button
                  onClick={() => setEditing(editing === doc.id ? null : doc.id)}
                  title="Edit details or move to another folder"
                  className="text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 text-sm px-1"
                >
                  ✎
                </button>
                <button
                  onClick={() => remove(doc)}
                  disabled={busy === doc.id}
                  title="Delete"
                  className="text-slate-500 hover:text-red-600 dark:hover:text-red-400 text-sm px-1"
                >
                  🗑
                </button>
              </div>
            </div>
            {editing === doc.id && (
              <DocumentEditor
                doc={doc}
                entities={entities}
                accounts={accounts}
                onSaved={() => {
                  setEditing(null)
                  onChanged()
                }}
                onCancel={() => setEditing(null)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Tab ─────────────────────────────────────────────────────

export default function DocumentsTab() {
  const [docs, setDocs] = useState<FinanceDocument[]>([])
  const [entities, setEntities] = useState<BusinessEntity[]>([])
  const [accounts, setAccounts] = useState<FinancialAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [folder, setFolder] = useState<DocumentFolder | 'all'>('all')
  const [subFolder, setSubFolder] = useState<string>('all')
  const [query, setQuery] = useState('')
  const [loadError, setLoadError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      const [d, e, a] = await Promise.all([listDocuments(), listEntities(), listAccounts()])
      setDocs(d)
      setEntities(e)
      setAccounts(a)
      setLoadError(null)
    } catch (err) {
      setLoadError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  const counts = useMemo(() => {
    const map: Record<string, number> = { all: docs.length }
    FOLDERS.forEach((f) => (map[f] = docs.filter((d) => d.folder === f).length))
    return map
  }, [docs])

  // Sub-folders are the natural second level of each top folder: tax year,
  // business, account.
  const subFolders = useMemo(() => {
    if (folder === 'taxes') {
      return Array.from(new Set(docs.filter((d) => d.folder === 'taxes').map((d) => d.taxYear)))
        .sort((a, b) => (b ?? 0) - (a ?? 0))
        .map((y) => ({ id: y === null ? 'none' : String(y), label: y === null ? 'No year' : `TY ${y}` }))
    }
    if (folder === 'businesses') {
      const used = new Set(docs.filter((d) => d.folder === 'businesses').map((d) => d.entityId))
      const list = entities
        .filter((e) => used.has(e.id))
        .map((e) => ({ id: e.id, label: `${e.icon} ${e.shortName}` }))
      if (used.has(null)) list.push({ id: 'none', label: 'Unassigned' })
      return list
    }
    if (folder === 'accounts') {
      const used = new Set(docs.filter((d) => d.folder === 'accounts').map((d) => d.accountId))
      const list = accounts
        .filter((a) => used.has(a.id))
        .map((a) => ({ id: a.id, label: `${a.institutionName} — ${a.name}` }))
      if (used.has(null)) list.push({ id: 'none', label: 'Unassigned' })
      return list
    }
    return []
  }, [accounts, docs, entities, folder])

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return docs.filter((d) => {
      if (folder !== 'all' && d.folder !== folder) return false
      if (subFolder !== 'all') {
        const key =
          folder === 'taxes'
            ? d.taxYear === null ? 'none' : String(d.taxYear)
            : folder === 'businesses'
              ? d.entityId ?? 'none'
              : d.accountId ?? 'none'
        if (key !== subFolder) return false
      }
      if (!needle) return true
      return [d.name, d.fileName, d.source, d.notes, d.category]
        .join(' ')
        .toLowerCase()
        .includes(needle)
    })
  }, [docs, folder, query, subFolder])

  if (loadError) return <LoadError error={loadError} onRetry={reload} />
  if (loading) return <LoadingRows rows={6} />

  const sharedCount = docs.filter((d) => d.sharedWithCpa).length

  return (
    <div className="space-y-6">
      <Card
        title="Upload documents"
        subtitle="Statements, 1099s, K-1s, formation docs, receipts — dropped files are routed to a folder by filename and you confirm before they're filed"
      >
        <DocumentUploader entities={entities} accounts={accounts} onUploaded={reload} />
      </Card>

      <Card
        title="Document Vault"
        subtitle={`${docs.length} documents · ${sharedCount} shared with your CPA`}
        action={
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search documents…"
            className="bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-3 py-1.5 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none w-48"
          />
        }
      >
        {/* Top-level folders */}
        <div className="flex gap-2 flex-wrap mb-3">
          {(['all', ...FOLDERS] as Array<DocumentFolder | 'all'>).map((f) => (
            <button
              key={f}
              onClick={() => {
                setFolder(f)
                setSubFolder('all')
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                folder === f
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
              }`}
            >
              {f === 'all' ? '🗂️ All' : `${FOLDER_ICONS[f]} ${FOLDER_LABELS[f]}`}
              <span className="ml-1.5 opacity-60">{counts[f]}</span>
            </button>
          ))}
        </div>

        {/* Second level */}
        {subFolders.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mb-4 pl-1">
            <button
              onClick={() => setSubFolder('all')}
              className={`px-2.5 py-1 rounded-md text-xs transition-colors ${
                subFolder === 'all'
                  ? 'bg-slate-300 dark:bg-slate-600 text-slate-900 dark:text-white'
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              All
            </button>
            {subFolders.map((s) => (
              <button
                key={s.id}
                onClick={() => setSubFolder(s.id)}
                className={`px-2.5 py-1 rounded-md text-xs transition-colors ${
                  subFolder === s.id
                    ? 'bg-slate-300 dark:bg-slate-600 text-slate-900 dark:text-white'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        <DocumentList
          docs={filtered}
          entities={entities}
          accounts={accounts}
          onChanged={reload}
          emptyMessage={
            query
              ? `Nothing matches “${query}” in this folder.`
              : 'No documents in this folder yet — drop one in the uploader above.'
          }
        />
      </Card>
    </div>
  )
}
